import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  Share2,
  Copy,
  CheckCircle,
  Download,
  Linkedin,
  Instagram,
  MessageCircle,
  Mail,
} from "lucide-react";
import type { FinalResults } from "../types";
import { getMotivationalSharePhrase } from "../utils/sharePhrases";

interface ScoreShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: FinalResults;
}

const ScoreShareModal: React.FC<ScoreShareModalProps> = ({
  isOpen,
  onClose,
  results,
}) => {
  const [copied, setCopied] = useState(false);
  const { t, i18n } = useTranslation();
  const { userInfo } = results || {};
  const { positionTitle, city } = userInfo || {};
  if (!isOpen) return null;
  const score = results.globalScore;
  const scorePhrase = getMotivationalSharePhrase(i18n.language, score);

  // const getScoreEmoji = (score: number): string => {
  //   if (score >= 80) return "🎯";
  //   if (score >= 60) return "👍";
  //   if (score >= 40) return "🤔";
  //   return "😕";
  // };

  // const getScoreMessage = (score: number): string => {
  //   if (score >= 80) return t("score_share.excellent");
  //   if (score >= 60) return t("score_share.good");
  //   if (score >= 40) return t("score_share.moderate");
  //   return t("score_share.weak");
  // };

  const shareTexts = {
    general: t("score_share.share_text", {
      score,
      message: scorePhrase,
    }),
    linkedin: t("score_share.share_text_linkedin", {
      score,
      message: scorePhrase,
    }),
    instagram: t("score_share.share_text_instagram", {
      score,
      message: scorePhrase,
    }),
    twitter: t("score_share.share_text_twitter", {
      score,
      message: scorePhrase,
    }),
    email: t("score_share.share_text_email", {
      score,
      message: scorePhrase,
    }),
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const shareOnPlatform = (platform: string) => {
    const text =
      shareTexts[platform as keyof typeof shareTexts] || shareTexts.general;

    switch (platform) {
      case "linkedin": {
        const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          "https://www.cvmatch.io"
        )}&text=${encodeURIComponent(text)}`;
        window.open(linkedinUrl, "_blank");
        break;
      }
      case "instagram": {
        copyToClipboard(text);
        generateScoreImage();
        const instagramUrl = `https://www.instagram.com`;
        if (confirm(t("score_share.instagram_instructions"))) {
          window.open(instagramUrl, "_blank");
        }
        break;
      }
      case "twitter": {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          text
        )}`;
        window.open(twitterUrl, "_blank");
        break;
      }
      case "email": {
        const emailSubject = t("score_share.email_subject", { score });
        const emailBody = t("score_share.email_body", { text });
        
        // Copy email content to clipboard first
        const emailContent = `${emailSubject}\n\n${emailBody}`;
        copyToClipboard(emailContent);
        
        // Try to open email client with a simple approach
        const mailtoUrl = `mailto:?subject=${encodeURIComponent(
          emailSubject
        )}&body=${encodeURIComponent(emailBody)}`;
        
        // Use a more reliable method - create a temporary link and click it
        const link = document.createElement('a');
        link.href = mailtoUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // Use setTimeout to ensure the link is properly added to DOM
        setTimeout(() => {
          try {
            link.click();
          } catch (error) {
            console.log("Email client couldn't be opened:", error);
          } finally {
            // Clean up
            if (document.body.contains(link)) {
              document.body.removeChild(link);
            }
          }
        }, 100);
        
        // Show success message
        setTimeout(() => {
          alert(`${t("score_share.email_copied_success")}\n\n${t("score_share.email_subject_label", { subject: emailSubject })}`);
        }, 200);
        break;
      }

      default:
        copyToClipboard(text);
    }
  };

  const generateScoreImage = () => {
    // Create canvas to generate score image
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = 800;
    canvas.height = 600;

    // Gradient background
    const gradient = ctx.createLinearGradient(0, 0, 800, 600);
    gradient.addColorStop(0, "#6366f1");
    gradient.addColorStop(1, "#8b5cf6");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 800, 600);

    // Main text
    ctx.fillStyle = "white";
    ctx.font = "bold 48px Arial";
    ctx.textAlign = "center";
    ctx.fillText(t("score_share.image.title"), 400, 150);

    // Score
    ctx.font = "bold 120px Arial";
    ctx.fillText(`${score}%`, 400, 300);

    // Emoji and message
    ctx.font = "bold 36px Arial";
    ctx.fillText(`${scorePhrase}`, 400, 380);

    // URL
    ctx.font = "24px Arial";
    ctx.fillText("cvmatch.io", 400, 480);

    // Logo/branding
    ctx.font = "bold 20px Arial";
    ctx.fillText(t("score_share.image.tagline"), 400, 520);

    // Download image
    const link = document.createElement("a");
    link.download = `cvmatch-score-${score}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const platforms = [
    {
      id: "linkedin",
      name: "LinkedIn",
      icon: <Linkedin className="w-5 h-5" />,
      color: "from-blue-600 to-blue-700",
      description: t("score_share.platforms.linkedin"),
    },
    {
      id: "instagram",
      name: "Instagram",
      icon: <Instagram className="w-5 h-5" />,
      color: "from-pink-500 to-purple-600",
      description: t("score_share.platforms.instagram"),
    },
    {
      id: "twitter",
      name: "Twitter/X",
      icon: <MessageCircle className="w-5 h-5" />,
      color: "from-gray-800 to-black",
      description: t("score_share.platforms.twitter"),
    },
    {
      id: "email",
      name: "Email",
      icon: <Mail className="w-5 h-5" />,
      color: "from-green-500 to-green-600",
      description: t("score_share.platforms.email"),
    },
  ];
  const getScoreColor = (score: number): string => {
    if (score >= 80) return "#4caf50";
    if (score >= 60) return "#ff9800";
    if (score >= 40) return "#ff5722";
    return "#f44336";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <Share2 className="w-6 h-6 text-indigo-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {t("score_share.title")}
              </h2>
              <p className="text-gray-600 mt-1">{t("score_share.subtitle")}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Score preview */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white text-center mb-6">
            {positionTitle && (
              <div className="text-2xl mb-2">
                {t("score_share.position") + positionTitle}
                {city ? `- ${city}` : ""}
              </div>
            )}
            <div className="relative w-36 h-36 mx-auto mb-6">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#e0e0e0"
                  strokeWidth="10"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke={getScoreColor(score)}
                  strokeWidth="10"
                  strokeDasharray="282.743"
                  strokeDashoffset={282.743 - (282.743 * score) / 100}
                  strokeLinecap="round"
                  transform="rotate(-90 50 50)"
                  className="transition-all duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold">{score}%</span>
              </div>
            </div>
            <div className="text-2xl mb-2">{scorePhrase}</div>
            <div className=" font-bold">{t("score_share.subtext")}</div>
          </div>

          {/* Sharing platforms */}
          <div className="space-y-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("score_share.choose_platform")}:
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {platforms.map(platform => (
                <button
                  key={platform.id}
                  onClick={() => shareOnPlatform(platform.id)}
                  className={`p-4 rounded-xl bg-gradient-to-r ${platform.color} text-white hover:shadow-lg transition-all transform hover:scale-105 flex items-center`}
                >
                  <div className="mr-3">{platform.icon}</div>
                  <div className="text-left">
                    <div className="font-semibold">{platform.name}</div>
                    <div className="text-sm opacity-90">
                      {platform.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Additional options */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t("score_share.additional_options")}:
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => copyToClipboard(shareTexts.general)}
                className="p-4 border-2 border-gray-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all flex items-center"
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <div className="text-left">
                      <div className="font-semibold text-green-600">
                        {t("score_share.text_copied")} !
                      </div>
                      <div className="text-sm text-gray-600">
                        {t("score_share.paste_anywhere")}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5 text-gray-600 mr-3" />
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">
                        {t("score_share.copy_text")}
                      </div>
                      <div className="text-sm text-gray-600">
                        {t("score_share.for_manual_share")}
                      </div>
                    </div>
                  </>
                )}
              </button>

              <button
                onClick={generateScoreImage}
                className="p-4 border-2 border-gray-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all flex items-center"
              >
                <Download className="w-5 h-5 text-gray-600 mr-3" />
                <div className="text-left">
                  <div className="font-semibold text-gray-900">
                    {t("score_share.download_image")}
                  </div>
                  <div className="text-sm text-gray-600">
                    {t("score_share.visual_of_score")}
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Message preview */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className=" text-gray-900 mb-2">
              {t("score_share.cvMatchPartnerShip")}
              <a
                href="https://www.instagram.com/travail.en.suisse/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500"
              >
                Travail.en.Suisse
              </a>
            </h4>
            <div className="text-sm text-gray-700 whitespace-pre-line">
              {t("score_share.testYourResumeNow")}
              <a
                href="https://cvmatch.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500"
              >
                CVMatch | P-Tron Tech
              </a>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <p>
                💡 <strong>{t("score_share.tip.title")}:</strong>{" "}
                {t("score_share.tip.description")}
              </p>
            </div>

            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              {t("score_share.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreShareModal;
